import moment from 'moment';
import parsePhoneNumberFromString, {
  CountryCallingCode,
  CountryCode,
  getCountryCallingCode
} from 'libphonenumber-js';
import {
  isArray,
  isDate,
  isEmail,
  isInt,
  isNumber,
  isNumberString,
  isObject,
  isPhoneNumber,
  isString
} from 'class-validator';
import * as xss from 'xss';

import { ISO3166FilteredCountries } from '../country';
import { defaultPagination, Pagination } from '../pagination';
import { DateTime } from 'luxon';

export * as json from './json';
export * as promise from './promise';
export * as str from './str';
export * as math from './math';
export * as obj from './obj';
export * as arr from './array';
export * as misc from './misc';
export { getEnvVar } from './env';

export const DEFAULT_PHONE_COUNTRY = 'US';

export function sortByCaseInsensitive(array: any[], key: string): any[] {
  if (!array) {
    return array;
  }
  if (!Array.isArray(array)) {
    return array;
  }
  if (array.length === 0) {
    return array;
  }
  const compare = (k: string) => (a: any, b: any) => {
    const compareA = typeof a[k] === 'string' ? a[k].toLowerCase() : a[k];
    const compareB = typeof b[k] === 'string' ? b[k].toLowerCase() : b[k];
    return compareA > compareB ? 1 : compareB > compareA ? -1 : 0;
  };
  return array.concat().sort(compare(key));
}

export function toCamelCase(str: string): string {
  return str
    .toLowerCase()
    .replace(/^\w|[A-Z]|\b\w/gu, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/gu, '');
}

export function breakWordsAtCaps(str: string): string {
  return str
    .replace(/(?<caps>[A-Z])/gu, ' $1')
    .replace(/\s+/gu, ' ')
    .trim();
}

export function objectFilter<T extends object>(
  obj: T,
  predicate: (key: keyof T & string) => boolean
): T {
  // Be careful with the predicate, it can filter 0 out
  const values = obj as Record<string, unknown>;
  return Object.keys(obj)
    .filter(key => predicate(key as keyof T & string))
    .reduce<T>((res, key) => ({ ...res, [key]: values[key] }), {} as T);
}

// CAUTION! This is not a pure function - it mutates the
// input argument "in-place".
export function deleteFields(obj: any, badFields: string[]): void {
  // Recurse into sub-object fields if they are arrays or of type "object"
  Object.keys(obj).forEach(field => {
    if (obj[field] && (Array.isArray(obj[field]) || typeof obj[field] === 'object')) {
      deleteFields(obj[field], badFields);
    }
  });

  badFields.forEach(field => {
    delete obj[field];
  });
}

export const SENSITIVE_FIELDS = ['password', 'ssn'] as const;

export const HIDDEN_FIELDS = ['searchableCustomFields'] as const;

// CAUTION! This is not a pure function - it mutates the
// input argument "in-place".
export function deleteSensitiveFields(obj: any): void {
  deleteFields(obj, [...SENSITIVE_FIELDS]);
}

// Strips both sensitive and internal-only fields from outgoing payloads
// (websocket events, audit logs, etc.)
export function deleteHiddenFields(obj: any): void {
  deleteFields(obj, [...SENSITIVE_FIELDS, ...HIDDEN_FIELDS]);
}

// CAUTION: This is EXPERIMENTAL!
// Checks to see if the field exists anywhere in the object, at any nested level.
export function hasNestedField(obj: any, field: string): boolean {
  return obj && JSON.stringify(obj).includes(`"${field}":`);
}

export function isValidInputUrl(value: string): boolean {
  const validProtocols = ['http:', 'https:'];

  let url: URL;
  try {
    url = new URL(value);
  } catch (_) {
    return false;
  }

  // No quotes allowed, even encoded
  if (value.match(/"/gu) || value.match(/%22/gu)) {
    return false;
  }

  return validProtocols.includes(url.protocol);
}

/**
 * @deprecated Use chronon's formatDateTimeWithMilitarySupport or DateTime.fromISO().toFormat() directly
 * This function now expects Luxon format strings from LuxonDateTimeFormats enum
 * */
export function getFormattedTime(timestamp: string | Date, format: string, locale = 'en'): string {
  try {
    const date = isString(timestamp) ? DateTime.fromISO(timestamp) : DateTime.fromJSDate(timestamp);

    if (!date.isValid) {
      throw new Error(`Core: getFormattedTime(): Invalid date: ${timestamp}`);
    }

    return date.setLocale(locale).toFormat(format);
  } catch (_) {
    return `Core: getFormattedTime(): Invalid date ${timestamp} or format ${format}`;
  }
}

export function removeArrayItem(array: any[], item: any): void {
  const idx = array.indexOf(item);
  if (idx > -1) {
    array.splice(idx, 1);
  }
}

export function sumArray(array: any[], field: string | null, start = 0): number {
  return array.reduce((prev, cur) => prev + (parseFloat(field ? cur[field] : cur) || 0), start);
}

export function isEmptyObject(value: any): boolean {
  return (
    value && Object.keys(value).length === 0 && Object.getPrototypeOf(value) === Object.prototype
  );
}

export function isTrulyEmpty(value: unknown): boolean {
  // eslint-disable-next-line no-undefined
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return typeof value === 'object' && isEmptyObject(value);
}

export function formatMinutesToHuman(minutes: number | string): string {
  const totalMinutes = typeof minutes === 'number' ? minutes : parseInt(String(minutes), 10);
  const safeMinutes = isNaN(totalMinutes) ? 0 : totalMinutes;

  const hours = Math.floor(safeMinutes / 60);
  const remainingMinutes = safeMinutes % 60;

  const hourLabel = hours === 1 ? 'hour' : 'hours';
  const minuteLabel = remainingMinutes === 1 ? 'minute' : 'minutes';

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours} ${hourLabel} and ${remainingMinutes} ${minuteLabel}`;
  } else if (hours > 0) {
    return `${hours} ${hourLabel}`;
  }
  return `${remainingMinutes} ${minuteLabel}`;
}

export function ucWords(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map(s => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');
}

export function camelCaseToHumanReadable(str: string) {
  if (str) {
    return String(str)
      .match(/^[a-z]+|[A-Z][a-z]*/gu)
      ?.map(x => x[0].toUpperCase() + x.substring(1).toLowerCase())
      .join(' ');
  }
}

export function naturalSort<T>(items: T[], sortDesc?: boolean, objKey?: null | undefined): T[];
export function naturalSort<T extends Record<string, unknown>, K extends keyof T & string>(
  items: T[],
  sortDesc: boolean | undefined,
  objKey: K
): T[];
export function naturalSort<T>(
  items: T[],
  sortDesc = false,
  objKey: (keyof T & string) | null | undefined = null
): T[] {
  const collator = new Intl.Collator('en', {
    numeric: true,
    sensitivity: 'base'
  });
  const sorted = items.sort((a, b) => {
    const left = objKey ? (a as Record<string, unknown>)[objKey] : a;
    const right = objKey ? (b as Record<string, unknown>)[objKey] : b;
    return collator.compare(left as string, right as string);
  });

  if (sortDesc) {
    sorted.reverse();
  }

  return sorted;
}

export function randomString(length: number): string {
  if (isNumberString(length)) {
    length = Number(length);
  }
  if (!isNumber(length)) {
    length = 6;
  }
  const randomBytes = require('randombytes');
  return randomBytes(length).toString('hex').slice(0, length);
}

export function randomStringWithDigitsAndSymbols(length: number): string {
  const lowerCaseLetters = 'abcdefghijklmnopqrstuvwxyz';
  const upperCaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*()_+-=';

  const allChars = lowerCaseLetters + upperCaseLetters + numbers + symbols;

  let randomString = '';

  // Ensure that at least one lowercase, one uppercase, one digit and one symbol is generated
  randomString += lowerCaseLetters.charAt(Math.floor(Math.random() * lowerCaseLetters.length));
  randomString += upperCaseLetters.charAt(Math.floor(Math.random() * upperCaseLetters.length));
  randomString += numbers.charAt(Math.floor(Math.random() * numbers.length));
  randomString += symbols.charAt(Math.floor(Math.random() * symbols.length));

  // Fill the rest with random chars
  for (let i = 4; i < length; i++) {
    const randomNumber = Math.floor(Math.random() * allChars.length);
    randomString += allChars.substring(randomNumber, randomNumber + 1);
  }

  return randomString;
}

export function randomPassword(): string {
  return randomStringWithDigitsAndSymbols(32);
}

/**
 * Creates an unusable password.
 * @see https://github.com/django/django/blob/main/django/contrib/auth/hashers.py#L100-L119
 */
export function makeUnuseablePassword(): string {
  const unusablePasswordPrefix = '!';
  const unusablePasswordSuffixLength = 40;
  return `${unusablePasswordPrefix}${randomStringWithDigitsAndSymbols(
    unusablePasswordSuffixLength
  )}`;
}

type FalsyValue = false | 0 | 0n | '' | null | undefined;
type EmptyStringForFalsy<T> = T extends FalsyValue ? '' : T;
type SetNullsToEmptyStringResult<T> = T extends readonly (infer U)[]
  ? EmptyStringForFalsy<U>[]
  : { [K in keyof T]: EmptyStringForFalsy<T[K]> };

export function setNullsToEmptyString<T extends Record<string, unknown> | unknown[]>(
  data: T
): SetNullsToEmptyStringResult<T> {
  const data2 = data as SetNullsToEmptyStringResult<T>;

  for (const key in data) {
    if (!data[key]) {
      data2[key as keyof SetNullsToEmptyStringResult<T>] =
        '' as SetNullsToEmptyStringResult<T>[keyof SetNullsToEmptyStringResult<T>];
    }
  }

  return data2;
}

export function isPhoneValid(phoneNumber: string | number): boolean {
  const value = isInt(phoneNumber) ? String(phoneNumber) : phoneNumber;
  if (!isString(value)) {
    return false;
  }
  return value.startsWith('+') ? isPhoneNumber(value) : isPhoneNumber(`+1${value}`);
}

export function formatPhoneNumber(
  value: string,
  countryCode: CountryCode = DEFAULT_PHONE_COUNTRY
): string {
  return isString(value)
    ? parsePhoneNumberFromString(value, countryCode)?.formatInternational() || value
    : value;
}

export function formatPhoneNumberAsE164(
  value: string,
  countryCode: CountryCode = DEFAULT_PHONE_COUNTRY
) {
  // E.164 is a standard recommended by the International Telecommunication Union.
  // This format ensures that each PSTN (Public Switched Telephone Network) device
  // has a distinct and unique number worldwide.
  return isString(value)
    ? parsePhoneNumberFromString(value, countryCode)?.format('E.164') || value
    : value;
}

export function isValidDate(date: any) {
  return (
    (date && Object.prototype.toString.call(date) === '[object Date]' && !isNaN(date)) ||
    (typeof date === 'string' && moment(date, moment.ISO_8601, true).isValid())
  );
}

// TODO: Make this recursive!
// CAUTION: Mutates the input object!!
export function ensureDateTypes(obj: Record<string, unknown>): void {
  if (!isObject(obj)) {
    return;
  }
  const keys = Object.getOwnPropertyNames(obj);

  keys.forEach(key => {
    if (isValidDate(obj[key])) {
      obj[key] = new Date(obj[key] as string | number | Date);
    }
  });
}

export function sanitizeHTML(str: string): string {
  if (!isString(str)) {
    return str;
  }
  // First decode any HTML entities to handle encoded malicious content
  const decoded = str
    .replace(/&lt;/giu, '<')
    .replace(/&gt;/giu, '>')
    .replace(/&quot;/giu, '"')
    .replace(/&#0*34;/gu, '"')
    .replace(/&#x0*22;/giu, '"')
    .replace(/&apos;/giu, "'")
    .replace(/&#0*39;/gu, "'")
    .replace(/&#x0*27;/giu, "'")
    .replace(/&#x0*2F;/giu, '/')
    .replace(/&#0*60;/giu, '<')
    .replace(/&#x0*3c;/giu, '<')
    .replace(/&#0*62;/giu, '>')
    .replace(/&#x0*3e;/giu, '>')
    .replace(/&amp;/giu, '&'); // Must be last to avoid double-decoding

  const filter = new xss.FilterXSS({
    whiteList: {},
    stripIgnoreTag: true,
    stripIgnoreTagBody: ['script']
  });
  return filter.process(decoded).replace('[removed]', '');
}

export function sanitizeInput(str: string, shouldSanitizeWhitespace = true): string {
  const xssFilter = new xss.FilterXSS({
    whiteList: {
      div: [],
      a: ['href', 'target'],
      p: [],
      b: [],
      i: [],
      u: [],
      br: [],
      strong: []
    },
    stripIgnoreTag: true,
    stripIgnoreTagBody: ['script'],
    escapeHtml: value => value
  });
  const result = xssFilter.process(str).replace('[removed]', '');
  if (shouldSanitizeWhitespace) {
    return result.replace(/  +/gu, ' ').trim();
  }
  return result;
}

type RecursivelySanitized<T> = T extends string
  ? string
  : T extends readonly (infer U)[]
  ? RecursivelySanitized<U>[]
  : T extends object
  ? { [K in keyof T]: RecursivelySanitized<T[K]> }
  : T;

export function recursivelySanitizeValues<T>(
  value: T,
  shouldSanitizeWhitespace = true
): RecursivelySanitized<T> {
  if (Array.isArray(value)) {
    return value.map(v =>
      recursivelySanitizeValues(v, shouldSanitizeWhitespace)
    ) as RecursivelySanitized<T>;
  }
  if (isObject(value) && !isDate(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, value]) => [
        key,
        recursivelySanitizeValues(value, shouldSanitizeWhitespace)
      ])
    ) as RecursivelySanitized<T>;
  }
  if (isString(value)) {
    return sanitizeInput(value, shouldSanitizeWhitespace) as RecursivelySanitized<T>;
  }
  return value as RecursivelySanitized<T>;
}

export function sortObjectByKeys<T extends Record<string, unknown>>(
  obj: T | null | undefined
): Record<string, T[keyof T]> {
  return obj
    ? Object.keys(obj)
        .sort()
        .reduce<Record<string, T[keyof T]>>((o, k) => ({ ...o, [k]: obj[k as keyof T] }), {})
    : {};
}

export function shouldTruncateString(str: string, length = 50): boolean {
  return str?.length > length;
}

export function truncateString(str: string, length = 50, addEllipses = true): string {
  if (isString(str) || isNumber(str)) {
    const newStr = String(str);
    const shouldUseEllipses = addEllipses && shouldTruncateString(newStr, length);

    const ellipsis = '…';
    const adjustedLength = shouldUseEllipses ? length - ellipsis.length : length;
    return `${newStr.slice(0, adjustedLength)}${shouldUseEllipses ? ellipsis : ''}`;
  }
  return '';
}

export function isValidInternalEmail(email: string): boolean {
  if (isEmail(email)) {
    const allowedDomains = ['loadsmart.', 'opendock.'];
    const splitEmail = email.split('@');

    if (splitEmail.length === 2) {
      const username = splitEmail[0];
      const domain = splitEmail[1];
      if (allowedDomains.find(d => domain.includes(d))) {
        if (username.includes('internal')) {
          return true;
        }
      }
    }
  }

  return false;
}

// I Hate this, but there isn't a good way to determine this if we only have the URL
export function isImageUrl(fileUrl: string): boolean {
  if (!isString(fileUrl)) {
    return false;
  }

  const imageExtensions = ['jpg', 'jpeg', 'png'];
  const fileExtension = fileUrl.split('.').pop() ?? '';
  return imageExtensions.includes(fileExtension);
}

/**
 * Returns the first array element whose JSON serialization equals that of `obj`, or `undefined`.
 * Comparison is via `JSON.stringify` (not deep structural equality).
 */
export function objExistsInArr<T>(
  arr: readonly T[] | null | undefined,
  obj: unknown
): T | undefined {
  // eslint-disable-next-line no-undefined
  if (arr === null || arr === undefined) {
    throw new Error('Core: objExistsInArr(): expected an array');
  }
  const objString = JSON.stringify(obj);
  return arr.find(item => JSON.stringify(item) === objString);
}

export function leftPadString(
  input: string | number,
  charactersToPad = 1,
  padCharacter = '0',
  maxCharacters: number | null = null
): string {
  const str = input.toString();
  maxCharacters = maxCharacters ?? str.length + charactersToPad;
  return str.padStart(maxCharacters, padCharacter);
}

export function roundToNearestNumber(num: number, multiple: number, roundUp = true): number {
  if (roundUp) {
    return Math.ceil(num / multiple) * multiple;
  }
  return Math.floor(num / multiple) * multiple;
}

export function toKebabCase(str: string): string {
  if (!str) {
    return str;
  }
  return str
    .replace(/\s+/gu, '-') // Replace whitespace with dashes
    .replace(/:/gu, '') // Remove colons
    .replace(/,/gu, '') // Remove commas
    .replace(/(?<lowercase>[a-z])(?<uppercase>[A-Z])/gu, '$1-$2') // Convert camelCase to kebab-case
    .toLowerCase(); // Convert to lowercase
}

export function snakeToTitleCase(str: string): string {
  return (
    str
      // eslint-disable-next-line require-unicode-regexp
      .replace(/^[-_]*(?<temp1>.)/, (_, c) => c.toUpperCase())
      // eslint-disable-next-line require-unicode-regexp
      .replace(/[-_]+(?<temp1>.)/g, (_, c) => ` ${c.toUpperCase()}`)
  );
}

export function tryParseJson<T = any>(jsonString: string): T | null {
  if (jsonString && typeof jsonString === 'object') {
    return jsonString;
  }

  try {
    return JSON.parse(jsonString);
  } catch (e) {
    return null;
  }
}

export function getCountryCallingCodes(): {
  iso2: string;
  name: string;
  callingCode: CountryCallingCode;
}[] {
  return Object.values(ISO3166FilteredCountries).map(country => ({
    iso2: country.iso2,
    name: country.name,
    callingCode: getCountryCallingCode(country.iso2 as CountryCode)
  }));
}

export function removeNonAlphaNumericChars(str: string): string {
  return str.replace(/[^a-z0-9]/giu, '');
}

export function insertStringIntoString(
  str: string,
  strToInsert: string,
  insertionIndex: number
): string {
  return `${str.slice(0, insertionIndex)}${strToInsert}${str.slice(insertionIndex)}`;
}

export function pluralize(count: number, word: string, plural?: string): string {
  plural = plural ?? `${word}s`;
  return Math.abs(count) === 1 ? word : plural;
}

/**
 * Depth-first removal of every own property named `keyToRemove` on `obj` and nested plain objects/arrays.
 * Mutates `obj` in place and returns the same reference.
 */
export function recursivelyRemoveKeys<T extends object>(obj: T, keyToRemove: string): T {
  const record = obj as Record<string, unknown>;
  for (const key in record) {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      const value = record[key];
      if (typeof value === 'object' && value !== null) {
        recursivelyRemoveKeys(value as object, keyToRemove);
      }

      if (key === keyToRemove) {
        delete record[key];
      }
    }
  }
  return obj;
}

type PaginateArrayResult<T = any> = {
  data: T[];
  total: number;
  count: number;
  page: number;
  pageCount: number;
};

export function paginateArray<T = any>(
  arr: T[],
  opts?: Partial<Pagination>
): PaginateArrayResult<T> {
  if (!isArray(arr)) {
    throw new Error(`Core: paginateArray(): expected array and got ${typeof arr}`);
  }

  const size = Math.min(opts?.size ?? defaultPagination.size, defaultPagination.size);
  const total = arr.length;
  const pageCount = Math.ceil(total / size);
  const page = Math.min(Math.max(opts?.page ?? defaultPagination.page, 1), pageCount);
  const start = (page - 1) * size;
  const end = size + start;
  const data = arr.slice(start, end);
  const count = data.length;
  return { data, total, count, page, pageCount };
}

/**
 * Joins multiple names into a single full name.
 */
export function namesToFullName(...names: Array<string | undefined>): string {
  return names
    .flatMap(s => s?.split(' ') ?? [])
    .map(s => s?.trim())
    .filter(s => (s?.length ?? 0) > 0)
    .join(' ');
}

/**
 * Transforms a string into a query object that can be used with NestJS Crud.
 */
export function searchStringToQuery(
  searchStr: string,
  {
    searchableFields = [],
    searchFieldsToSplit = []
  }: {
    searchableFields?: string[];
    searchFieldsToSplit?: string[];
  } = {}
) {
  const filters = searchStr
    ? searchableFields.reduce((acc, curr) => {
        const splitted = searchFieldsToSplit.includes(curr)
          ? [{ [curr]: { $inL: searchStr?.toLowerCase()?.split(' ') } }]
          : [];
        return [...acc, { [curr]: { $contL: searchStr } }, ...splitted];
      }, [] as Record<string, unknown>[])
    : [];
  return filters;
}

export function sortByArray<T>(arrayToSort: T[], sortOrder: readonly T[]): T[] {
  const orderMap = new Map<T, number>();
  sortOrder.forEach((item, index) => orderMap.set(item, index));

  return arrayToSort.sort((a, b) => {
    const indexA = orderMap.get(a) ?? Infinity;
    const indexB = orderMap.get(b) ?? Infinity;
    return indexA - indexB;
  });
}
